import { Outlet } from "react-router";
import { useLocation, useNavigate } from "react-router";
import ChartPage from "./charts";
import { SignedIn, useAuth } from "@clerk/clerk-react";
import { useEffect } from "react";
import NavBar from "@/components/NavBar";
import ProgressBar from "@/components/ProgressBar";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { Toaster } from "@/components/ui/toaster";
import { useTanFetch } from "@/hooks/useTanFetch";
import { ApiResponse, User } from "@/types";

const isLocal = import.meta.env.MODE === "development";
const CLERK_KEY = isLocal
  ? import.meta.env.VITE_CLERK_PUBLISHABLE_KEY_DEV
  : import.meta.env.VITE_CLERK_PUBLISHABLE_KEY_PROD;

if (!CLERK_KEY) {
  throw new Error("Missing Publishable Key");
}

export default function Root() {
  const { userId, isLoaded } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery("mobile");
  const theme = localStorage.getItem("vite-ui-theme");

  const { data: userData, isLoading: userDataLoading } = useTanFetch<ApiResponse<User>>(
    ["user"],
    true,
  );

  const isSubscribed =
    !userDataLoading &&
    isLoaded &&
    ((userId && (userData.data?.subscriptionStatus === "ACTIVE" || userData.data?.subscriptionStatus === "FREE")) ||
      location.state?.isSubscribed);


  useEffect(() => {
    // Only redirect to sign-in if Clerk says user is not authenticated AND not already on sign-in/sign-up page
    if (isLoaded && !userId && !location.pathname.includes("/sign-in") && !location.pathname.includes("/sign-up")) {
      navigate("/sign-in");
      return;
    }

    // If user is signed in but trying to access sign-in or sign-up, redirect to home
    if (isLoaded && userId && (location.pathname.includes("/sign-in") || location.pathname.includes("/sign-up"))) {
      navigate("/");
      return;
    }

    // Skip subscription redirect for FREE and ACTIVE users
    // Only redirect to /subscribe if user has no subscription status or null status
    if (isLoaded && !userDataLoading && userId && userData.data &&
        !userData.data.subscriptionStatus && !location.pathname.includes("/subscribe")) {
      navigate("/subscribe");
      return;
    }

    // If ACTIVE (paid) subscriber is on subscribe page, redirect to home
    // But allow FREE tier users to access the subscribe page for upgrades
    if (
      isLoaded &&
      !userDataLoading &&
      userId &&
      userData.data?.subscriptionStatus === "ACTIVE" &&
      location.pathname.includes("/subscribe")
    ) {
      navigate("/");
      return;
    }
  }, [userId, isSubscribed, isLoaded, userData, location.pathname, navigate, userDataLoading]);

  if (!isLoaded || userDataLoading) {
    return <ProgressBar />;
  }

  const fullWidthPages = ["sign-in", "sign-up", "subscribe"];
  const isFw = fullWidthPages.includes(location.pathname.split("/")[1]);

  return (
    <>
      <div
        className={`${theme} !font-dank ${userId && isSubscribed ? "grid grid-cols-1 md:grid-cols-8 h-screen" : "h-full w-full grid grid-cols-1"} ${!isFw && "max-w-[1400px]"} mx-auto text-primary-foreground`}
      >
        {isSubscribed && (
          <SignedIn>
            <NavBar />
          </SignedIn>
        )}

        <div className={`col-span-1 md:col-span-7 ${isMobile && "pb-16"}`}>
          {location.pathname === "/" ? <ChartPage /> : <Outlet />}
        </div>
        <Toaster />
      </div>
    </>
  );
}
